<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import CustomIcon from '@/_company/components/CustomIcon.vue';
import { useCloseOnEscape } from '@/_company/composables/useCloseOnEscape';

const store = useStore();
const isOpen = computed(() => store.state.common.sidebar.isOpen);

const closeSidebar = () => store.dispatch('common/closeSidebar');
useCloseOnEscape(() => closeSidebar);
</script>

<template>
  <div class="sidebar-container">
    <section class="sidebar" :class="{ closed: !isOpen }">
      <div class="sidebar-inner">
        <header class="header">
          <div class="header-text">
            <h3>
              <slot name="header" />
            </h3>

            <p>
              <slot name="sub-header" />
            </p>
          </div>

          <a @click="closeSidebar()">
            <CustomIcon icon="arrow" :additionalClasses="{ 'close-icon': true }" :width="24" />
          </a>
        </header>

        <div>
          <slot name="links" />
        </div>

        <div class="logo">
          <slot name="logo" />
        </div>
      </div>
    </section>

    <Transition name="fade">
      <div class="overlay" v-if="isOpen" @click="closeSidebar()" />
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-container {
  width: fit-content;
}

.sidebar {
  height: 100%;
  border-right: 1px solid $secondary-border;
  padding: $space-24;
  width: $sidebar-width;
  transition: left 0.5s ease-in-out;
  background: $primary-background;
  position: relative;

  @include max-width-tablet-large {
    min-height: 100vh;
    position: fixed;
    padding: $space-12;
    top: 0;
    left: 0;
    z-index: 999;

    &.closed {
      left: -$sidebar-width;
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $space-24;

    @include max-width-tablet-large {
      margin-top: $space-24;
    }

    .header-text {
      h3 {
        @include primary-text-medium;
        margin: $space-4 0 0;
      }

      p {
        @include secondary-text-medium;
        color: $secondary-text;
        margin: $space-4 0 0;

        &:empty {
          display: none;
        }
      }
    }

    :deep(.close-icon) {
      fill: $tertiary-foreground;
      background-color: $secondary-background;
      padding: 7px 10px 7px 8px;
      margin-right: 0;
      border-radius: 50%;
      cursor: pointer;

      @include tablet-large {
        display: none;
      }

      &:hover {
        background-color: darken(rgba(243, 243, 243, 1), 5%);
      }
    }
  }

  .logo {
    position: absolute;
    bottom: $space-24;
    left: $space-24;
  }
}

.overlay {
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  display: block;

  @include tablet-large {
    display: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
